import React, { useEffect, useRef, useState } from 'react'
import Menu from 'components/menu'
import { IContextMenuState } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { KEY_PRESS } from 'components/spreadsheet/constants/const'
import { tv } from 'tailwind-variants'
import {
  MentionMember,
  markdownToEditorText,
  replaceMentionEditorRange,
  updateMentionEditorText
} from 'helpers/mentions'
import { validateSourceUrl } from 'helpers/utils'

const commentEditor = tv({
  slots: {
    // whitespace-pre-wrap: the comment popup (Menu) sets white-space: nowrap, which is
    // inherited; force normal wrapping so long comments wrap instead of overflowing.
    // [font-family:inherit]: no Tailwind preflight in this app, so the textarea would
    // otherwise fall back to the browser's monospace default.
    textarea:
      'w-full box-border min-h-16 max-h-50 px-2.5 py-2 [font-family:inherit] text-sm leading-[1.45] whitespace-pre-wrap break-words text-body bg-surface border border-solid border-divider rounded-md resize-y outline-none transition-[border-color,box-shadow] duration-150 ease-in-out placeholder:text-faint focus:border-blue focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)]',
    list: 'list-none m-0 px-0 py-1',
    option: 'flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-surface-sunken',
    name: 'font-medium'
  },
  variants: {
    active: {
      true: { option: 'bg-surface-sunken' }
    }
  }
})

const { textarea: textareaClass, list, option, name } = commentEditor()

interface CommentEditorProps {
  value: string
  onChange: (value: string) => void
  members: MentionMember[]
  placeholder?: string
  id?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  focusOnMount?: boolean
}

// Matches the mention token currently being typed at the caret: an `@`
// preceded by start-of-string or whitespace, followed by non-space chars
// up to the caret. Capture group 1 is the query (may be empty).
const ACTIVE_MENTION_REGEX = /(?:^|\s)@([^\s]*)$/

interface ActiveMention {
  query: string
  // Index of the `@` in the full text, so we know what substring to replace.
  atIndex: number
}

const getActiveMention = (value: string, caret: number): ActiveMention | null => {
  const beforeCaret = value.slice(0, caret)
  const match = beforeCaret.match(ACTIVE_MENTION_REGEX)
  if (!match) {
    return null
  }
  const query = match[1]
  // The matched substring may start with the leading whitespace; the `@` sits
  // at the end of that substring minus the query length and the `@` itself.
  const atIndex = caret - query.length - 1
  return { query, atIndex }
}

const filterMembers = (members: MentionMember[], query: string): MentionMember[] => {
  if (query === '') {
    return members
  }
  const lowered = query.toLowerCase()
  return members.filter((member: MentionMember): boolean => member.name.toLowerCase().includes(lowered))
}

const CommentEditor: React.FC<CommentEditorProps> = ({
  value,
  onChange,
  members,
  placeholder = 'Type your comment here...',
  id,
  onKeyDown,
  focusOnMount
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [menuState, setMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [menuWidth, setMenuWidth] = useState<number>(0)
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)

  // Imperative focus on mount (matches the repo convention; the autoFocus JSX
  // prop is disallowed by jsx-a11y/no-autofocus).
  useEffect((): void => {
    if (focusOnMount && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [focusOnMount])

  const filtered = activeMention ? filterMembers(members, activeMention.query) : []
  const isOpen = menuState.open && filtered.length > 0

  const closePopup = (): void => {
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
    setActiveMention(null)
    setActiveIndex(0)
  }

  const openPopupAtTextarea = (): void => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    const rect = textarea.getBoundingClientRect()
    // Match the dropdown width to the textarea so member names aren't clipped
    // (the Menu otherwise falls back to a narrow DEFAULT_MENU_WIDTH).
    setMenuWidth(Math.round(rect.width))
    setMenuState({
      ...INITIAL_CONTEXT_MENU_STATE,
      open: true,
      top: `${rect.bottom + 2}px`,
      left: `${rect.left}px`,
      right: 'auto',
      bottom: 'auto'
    })
  }

  const recomputeMention = (text: string, caret: number): void => {
    const mention = getActiveMention(text, caret)
    if (mention) {
      setActiveMention(mention)
      setActiveIndex(0)
      openPopupAtTextarea()
    } else {
      closePopup()
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const text = event.target.value
    onChange(updateMentionEditorText(value, text))
    recomputeMention(text, event.target.selectionStart ?? text.length)
  }

  const selectMember = (member: MentionMember): void => {
    if (!activeMention) {
      return
    }
    const before = markdownToEditorText(value).slice(0, activeMention.atIndex)
    const caret = activeMention.atIndex + 1 + activeMention.query.length
    // Store the selected identity while displaying a readable name in the textarea.
    const inserted = `@${member.name} `
    const newValue = replaceMentionEditorRange(
      value,
      activeMention.atIndex,
      caret,
      `@[${member.name}](mention:${member.publicId}) `
    )
    onChange(newValue)
    closePopup()

    const newCaret = before.length + inserted.length
    // Restore focus + caret position after React re-renders the controlled value.
    requestAnimationFrame((): void => {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.focus()
        textarea.setSelectionRange(newCaret, newCaret)
      }
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (isOpen) {
      if (event.key === KEY_PRESS.down) {
        event.preventDefault()
        setActiveIndex((prev: number): number => (prev + 1) % filtered.length)
        return
      }
      if (event.key === KEY_PRESS.up) {
        event.preventDefault()
        setActiveIndex((prev: number): number => (prev - 1 + filtered.length) % filtered.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        selectMember(filtered[activeIndex])
        return
      }
      if (event.key === KEY_PRESS.esc) {
        event.preventDefault()
        closePopup()
        return
      }
    }
    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  return (
    <>
      <textarea
        id={id}
        data-testid="comment-editor-textarea"
        className={textareaClass()}
        placeholder={placeholder}
        value={markdownToEditorText(value)}
        ref={textareaRef}
        wrap="hard"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {isOpen && (
        <Menu
          id={`comment-editor-mentions${id ? `-${id}` : ''}`}
          menuState={menuState}
          setMenuState={setMenuState}
          width={menuWidth || undefined}
          onClose={closePopup}
        >
          <ul
            className={list()}
            onMouseDown={(event: React.MouseEvent<HTMLUListElement>): void => {
              // preventDefault keeps the textarea focused (blur would close the
              // popup before the click handler runs). stopPropagation keeps the
              // mousedown from reaching the comment popup's document-level
              // outside-click listener — the mention list is portaled outside
              // that popup's DOM subtree, so without this it reads as an outside
              // click and closes the whole comment box.
              event.preventDefault()
              event.stopPropagation()
            }}
          >
            {filtered.map((member: MentionMember, index: number) => (
              <li
                key={member.publicId}
                data-testid={`comment-editor-mention-${member.publicId}`}
                className={option({ active: index === activeIndex })}
                onClick={(): void => selectMember(member)}
                onMouseEnter={(): void => setActiveIndex(index)}
              >
                <img
                  src={validateSourceUrl(member.profilePicture)}
                  className="shrink-0 object-cover rounded-full w-5 h-5"
                  alt={member.name}
                />
                <span>
                  <span className={name()}>{member.name}</span>
                </span>
              </li>
            ))}
          </ul>
        </Menu>
      )}
    </>
  )
}

export default CommentEditor
